import React, { useMemo, useState } from "react";
import { Table, Tag, Typography, Divider, Spin } from "antd";
import { ReactComponent as Unbilled } from "../../assets/Unbilled.svg";
import { ReactComponent as Unpaid } from "../../assets/Unpaid.svg";
import { ReactComponent as MatterFund } from "../../assets/Allocated matter fund.svg";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";

const { Title, Text } = Typography;

const dmSans = {};

const fmt = (val) => (val !== undefined && val !== null && val !== "" ? val : "———");

// Safely resolve a display string for a "person"-like value that may be:
// - a plain string
// - an object like { name, emailid }
// - null/undefined
const personLabel = (person) => {
	if (!person) return "—";
	if (typeof person === "string") return person;
	if (typeof person === "object") {
		return person.name || person.emailid || person.email || "—";
	}
	return String(person);
};

const formatDate = (val) => {
	if (!val) return "———";
	const parts = String(val).split("-");
	if (parts.length === 3) {
		const [d, m, y] = parts;
		const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
		const monthName = months[parseInt(m, 10) - 1] || m;
		const day = parseInt(d, 10);
		const suffix =
			day === 1 || day === 21 || day === 31 ? "st"
				: day === 2 || day === 22 ? "nd"
					: day === 3 || day === 23 ? "rd"
						: "th";
		return `${day}${suffix} ${monthName} ${y}`;
	}
	return val;
};

const DetailField = ({ label, value }) => (
	<div style={{ marginBottom: 16 }}>
		<Text style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 3 }}>
			{label}
		</Text>
		<Text style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
			{value}
		</Text>
	</div>
);

export default function AdvisoryMatter({ matterData, loading }) {
	const userList = useSelector(getUserList);

	const [currencyView, setCurrencyView] = useState("local");

	const { key, url } = queryKeys.getClientMatterList(
		`?clientid=${matterData?.clientId}&matterid=${matterData?.matterId}`
	);
	const { data: clientMatterRes } = useFetch(key, url, {
		enabled: !!matterData?.clientId && !!matterData?.matterId,
	});

	const { key: matterUnbilledPaidKey, url: matterUnbilledPaidUrl } =
		queryKeys.getMatterUnbilledPaidCounts(`?matterid=${matterData?.matterId}`);
	const { data: matterUnbilledPaidRes } = useFetch(matterUnbilledPaidKey, matterUnbilledPaidUrl, {
		enabled: !!matterData?.clientId && !!matterData?.matterId,
	});

	const { key: billingKey, url: billingUrl } = queryKeys.getBillingById(
		`matterid=${matterData?.matterId}`
	);
	const { data: billingRes } = useFetch(billingKey, billingUrl, {
		enabled: !!matterData?.matterId,
	});

	const { key: claimsKey, url: claimsUrl } = queryKeys.getClaims(
		`?matterid=${matterData?.matterId}&clientId=${matterData?.clientId}&matterclaims=true`
	);
	const { data: claimsData } = useFetch(claimsKey, claimsUrl, {
		enabled: !!matterData?.matterId && !!matterData?.clientId,
	});

	const { key: actualSummaryKey, url: actualSummaryUrl } =
		queryKeys.getActualSummaryToEngagementId(matterData?.matterId);
	const { data: actualSummaryData } = useFetch(actualSummaryKey, actualSummaryUrl, {
		enabled: !!matterData?.matterId,
	});

	const actualSummaryTimeSheetData = useMemo(
		() => actualSummaryData?.response?.data ?? [],
		[actualSummaryData]
	);

	const { data: billingentity } = useFetch(queryKeys.getBillingEntityList.key, queryKeys.getBillingEntityList.url);

	if (loading && !matterData) {
		return (
			<div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
				<Spin size="large" />
			</div>
		);
	}

	const info = matterData?.information || {};
	const client = matterData?.client || {};
	const stakeHolder = matterData?.stakeHolder || {};
	const financial = matterData?.financial_resourcePlan || {};

	const resolveUserName = (id) => {
		if (!id) return "———";
		const found = userList.find((u) => u.userid === id || u.personalemailid === id);
		return found ? found.fullname : id;
	};

	const currency = financial.currency || "USD";
	const currencySymbol = currency === "USD" ? "$" : currency === "KES" ? "KES " : currency + " ";
	const conversionRate = Number(financial?.conversionRate) || 1;

	const isLocalCurrencyUSD = currency.toUpperCase() === "USD";

	const fmtUSD = (val) =>
		val !== undefined && val !== null && val !== ""
			? `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
			: `$0.00`;

	const fmtLocal = (val) =>
		val !== undefined && val !== null && val !== ""
			? `${currencySymbol}${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
			: `${currencySymbol}0.00`;

	const fmtCell = (usdValue) => {

		if (isLocalCurrencyUSD || currencyView === "usd") {
			return fmtUSD(usdValue);
		}
		// Convert USD → local
		const localValue = Number(usdValue);
		return fmtLocal(localValue);
	};

	const clientMatterFund = clientMatterRes?.response?.data?.filter(
		(t) => t.accounttype === "Client Account"
	);
	const allocatedMatterFund = Array.isArray(clientMatterFund)
		? clientMatterFund.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
		: 0;

	const matterUnbilledPaidCounts = matterUnbilledPaidRes?.response?.data;
	const billingData = billingRes?.response?.items || [];

	const clientName = fmt(client.clientName || matterData?.clientName);
	const proposalId = fmt(matterData?.proposalId);
	const practiceArea = fmt(info.practiceArea || matterData?.practiceArea);
	const clientReferenceNumber = fmt(client.clientReferenceNumber || matterData?.clientReferenceNumber);
	const locationVal = fmt(info.location || matterData?.location);
	const objective = fmt(info.objective || matterData?.objective);
	const leadAttorney = resolveUserName(stakeHolder.leadAttorney || matterData?.leadAttorney);
	const referringAttorney = resolveUserName(stakeHolder.referringAttorney || matterData?.referringAttorney);
	const planStartDate = formatDate(stakeHolder.planStartDate || matterData?.planStartDate);
	const planEndDate = formatDate(stakeHolder.planEndDate || matterData?.planEndDate);
	const legalFilingDeadline = formatDate(stakeHolder.legalFilingDeadline || matterData?.legalFilingDeadline);
	const deadlineMet = matterData?.deadlineMet;
	const usdAmount = currencyView === "local" ? Number(financial?.localCurrencyAmount) : Number(financial.usdAmount ?? matterData?.usdAmount ?? 0);
	const billingEntityId = parseInt(financial?.billingEntityId);

	const employeeLists = Array.from(
		new Map(
			(matterData?.employeeList ?? [])
				.filter((item) => item?.userId !== null)
				.map((item) => [item.resourceId, item])
		).values()
	) || [];

	const matched = [];
	actualSummaryTimeSheetData?.forEach((ts) => {
		ts?.actualTimesheet
			?.filter((entry) => entry.status === "approved")
			?.forEach((entry) => {
				const emp = employeeLists.find(
					(e) => ts?.userId === e?.userId && Number(entry?.resourceId) === e?.resourceId
				);
				const alreadyMatched = matched?.find(
					(m) => m?.userId === ts?.userId && m?.resourceId === entry?.resourceId
				);
				if (emp && !alreadyMatched) {
					matched?.push({ userId: ts?.userId, resourceId: entry?.resourceId, approvedHours: entry?.sum });
				}
			});
	});

	const actualChargeout = matched?.reduce((total, item) => {
		const emp = employeeLists?.find(
			(e) => e?.userId === item?.userId && Number(item?.resourceId) === e?.resourceId
		);
		const user = userList?.find(
			(user) => user?.userid?.toLowerCase() === item?.userId?.toLowerCase()
		);
		const rate = Number(emp?.resourceExtRate ?? user?.chargeoutrate ?? 0);
		const tsEntry = actualSummaryTimeSheetData?.find((ts) => ts?.userId === item?.userId);
		const approvedHours =
			tsEntry?.actualTimesheet
				?.filter(
					(ts) =>
						ts?.resourceId.toString() === item?.resourceId.toString() &&
						ts?.status === "approved"
				)
				?.reduce((acc, ts) => acc + (Number(ts?.sum) || 0), 0) || 0;
		return total + approvedHours * rate;
	}, 0);

	const derivedChargeout = currencyView === "local" ? (matterData?.resourceList || []).reduce((sum, r) => {
		const mandays = Number(r.personDay ?? r.mandays ?? 0);
		const rate = Number(r.resourceExtRate ?? 0);
		return sum + mandays * 8 * rate / conversionRate;
	}, 0) : (matterData?.resourceList || []).reduce((sum, r) => {
		const mandays = Number(r.personDay ?? r.mandays ?? 0);
		const rate = Number(r.resourceExtRate ?? 0);
		return sum + mandays * 8 * rate;
	}, 0);

	const plannedChargeout = derivedChargeout;

	const plannedSubcontractor = currencyView === "local" ? (matterData?.subcontractList || [])?.reduce(
		(sum, item) => sum + item.amount * Number(item.currencyrate) / conversionRate, 0
	) : (matterData?.subcontractList || [])?.reduce(
		(sum, item) => sum + item.amount * Number(item.currencyrate), 0
	);

	const profitLoss = usdAmount - (plannedChargeout + plannedSubcontractor);
	const pfaAmount = profitLoss < 0 ? Math.abs(profitLoss) : 0;
	const profitAfterPfa = profitLoss > 0 ? Math.abs(profitLoss) : 0;

	const totalLocalWithTax = billingData
		?.filter((item) => item.statusname === "Approved")
		.reduce((sum, b) => {
			const fee = Number(b.billingfee) || 0;
			const taxMultiplier = b.vat?.toUpperCase() === "VAT" ? Number(b.tax) / 100 : 1;
			return sum + fee + fee * taxMultiplier;
		}, 0) ?? 0;

	const actualBillingAmount = currencyView === "local" ? totalLocalWithTax : totalLocalWithTax * conversionRate;

	const billedClaimIds = new Set(
		billingData
			?.filter((item) => item.statusname === "Approved")
			?.flatMap((bill) => bill.claims.map((claim) => claim.claimsId))
	);
	const filteredClaimsData = claimsData?.response?.data?.filter(
		(claim) => !billedClaimIds?.has(claim.claimsid)
	);

	const VAT_RATE = billingentity?.response?.data?.find((be) => parseInt(be.id) === billingEntityId)?.tax / 100 || 1;

	const actualSubcontractTotal = filteredClaimsData
		?.filter((claim) => claim.type.toLowerCase() === "subcontract" && claim.status === "Unbilled")
		?.reduce((sum, claim) => {
			const amount = parseFloat(claim.totalamount || 0);
			// const vatMultiplier = claim.taxtype === "VAT" ? VAT_RATE : 0;
			return sum + amount;
		}, 0);

	const actualDisbursementTotal = filteredClaimsData
		?.filter((claim) => claim.type.toLowerCase() !== "subcontract" && claim.status === "Unbilled")
		?.reduce((sum, claim) => {
			const amount = parseFloat(claim.totalamount || 0);
			// const vatMultiplier = claim.taxtype === "VAT" ? VAT_RATE : 0;
			return sum + amount;
		}, 0);

	const actualDisbursement = currencyView === "local" ? Number(actualDisbursementTotal) : Number(actualDisbursementTotal) * conversionRate || 0;
	const actualSubcontract = currencyView === "local" ? Number(actualSubcontractTotal) : Number(actualSubcontractTotal) * conversionRate || 0;

	const approvedActualDisbursementSubcontract = billingData
		?.filter((item) => item.statusname === "Approved")
		?.reduce(
			(acc, item) => {
				const taxRate = parseFloat(item.tax) || 0;

				item.claims.forEach((claim) => {
					let amount = parseFloat(claim.amount) || 0;

					if (claim.vat === true) {
						amount = amount * (taxRate / 100);
					}

					if (claim.type.toLowerCase() === "subcontract") acc.subcontractTotal += amount;
					else acc.disbursementTotal += amount;
				});
				return acc;
			},
			{ subcontractTotal: 0, disbursementTotal: 0 }
		);

	const actualBillingDisbursement = currencyView === "local" ?
		Number(approvedActualDisbursementSubcontract?.disbursementTotal)
		:
		Number(approvedActualDisbursementSubcontract?.disbursementTotal) * conversionRate || 0;

	const actualBillingSubcontract = currencyView === "local" ?
		Number(approvedActualDisbursementSubcontract?.subcontractTotal)
		:
		Number(approvedActualDisbursementSubcontract?.subcontractTotal) * conversionRate || 0;

	const actualProfitLoss =
		(actualBillingAmount +
			actualBillingDisbursement +
			actualBillingSubcontract) -
		(actualChargeout +
			actualDisbursement + actualSubcontract);
	// ── Table columns (reactive to currencyView) ─────────────
	const budgetLabel = "Budget";
	const actualLabel = "Actual";

	const columns = [
		{
			title: "Entity",
			dataIndex: "entity",
			key: "entity",
			render: (text) => <Text style={{ fontSize: 13 }}>{text}</Text>,
		},
		{
			title: budgetLabel,
			dataIndex: "budget",
			key: "budget",
			align: "right",
			render: (text, record) =>
				record.profitHighlight ? (
					<Text style={{ color: record.isLoss ? "#dc2626" : "#16a34a", fontWeight: "bold", fontSize: 14 }}>
						{text}
					</Text>
				) : (
					<Text style={{ fontSize: 14 }}>{text}</Text>
				),
		},
		{
			title: actualLabel,
			dataIndex: "actual",
			key: "actual",
			align: "right",
			render: (text, record) =>
				record.profitHighlight && !record.isActualZero ? (
					<Text style={{ color: record.isActualLoss ? "#dc2626" : "#16a34a", fontWeight: "bold", fontSize: 13 }}>
						{text}
					</Text>
				) : (
					<Text style={{ fontSize: 13 }}>{text || ""}</Text>
				),
		},
	];

	// ── Table rows ────────────────────────────────────────────
	const tableData = [
		{
			key: "1", entity: "Billing Amount",
			budget: fmtCell(usdAmount, "budget"),
			actual: fmtCell(actualBillingAmount, "actual"),
			profitHighlight: false,
		},
		{
			key: "2", entity: "Billing Disbursement",
			budget: fmtCell(0, "budget"),
			actual: fmtCell(actualBillingDisbursement, "actual"),
			profitHighlight: false,
		},
		{
			key: "3", entity: "Billing Subcontract",
			budget: fmtCell(0, "budget"),
			actual: fmtCell(actualBillingSubcontract, "actual"),
			profitHighlight: false,
		},
		{
			key: "4", entity: "Chargeout",
			budget: fmtCell(plannedChargeout, "budget"),
			actual: fmtCell(actualChargeout, "actual"),
			profitHighlight: false,
		},
		{
			key: "5", entity: "Disbursement (Claims)",
			budget: fmtCell(0, "budget"),
			actual: fmtCell(actualDisbursement, "actual"),
			profitHighlight: false,
		},
		{
			key: "6", entity: "Subcontract (Claims)",
			budget: fmtCell(plannedSubcontractor, "budget"),
			actual: fmtCell(actualSubcontract, "actual"),
			profitHighlight: false,
		},
		{
			key: "7", entity: "Profit",
			budget: fmtCell(profitLoss, "budget"),
			actual: fmtCell(actualProfitLoss, "actual"),
			profitHighlight: true,
			isLoss: profitLoss < 0,
			isActualLoss: actualProfitLoss < 0,
			isActualZero: actualProfitLoss === 0,
		},
		{
			key: "8", entity: "PFA (Planned Fee Adjustment)",
			budget: fmtCell(pfaAmount, "budget"),
			actual: fmtCell(0, "actual"),
			profitHighlight: false,
		},
		{
			key: "9", entity: "Profit – after PFA",
			budget: fmtCell(profitAfterPfa, "budget"),
			actual: "",
			profitHighlight: false,
		},
	];

	// ── Stat cards ────────────────────────────────────────────
	const statCards = [
		{
			icon: <Unbilled style={{ fontSize: 20, color: "#2563eb" }} />,
			label: "Unbilled",
			value: fmtLocal(
				Number(matterUnbilledPaidCounts?.claimslogUnbilled) +
				Number(matterUnbilledPaidCounts?.timelogUnbilled)
			),
		},
		{
			icon: <Unpaid style={{ fontSize: 20, color: "#2563eb" }} />,
			label: "Unpaid",
			value: fmtLocal(Number(matterUnbilledPaidCounts?.unpaidAmount)),
		},
		{
			icon: <MatterFund style={{ fontSize: 20, color: "#2563eb" }} />,
			label: "Allocated Matter Fund",
			value: fmtLocal(allocatedMatterFund),
		},
	];

	// ── Styles ────────────────────────────────────────────────
	const cardStyle = {
		background: "#fff",
		border: "1px solid #e8e9ef",
		borderRadius: 12,
	};

	// ── Render ────────────────────────────────────────────────
	return (
		<>
			<div style={{ minHeight: "100vh", padding: "20px 0px", ...dmSans }}>
				<div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

					{/* LEFT */}
					<div style={{ flex: "0 0 57%" }}>

						{/* Stat cards */}
						<div style={{ display: "flex", gap: 14, marginBottom: 18 }}>
							{statCards.map((card, i) => (
								<div key={i} style={{ flex: 1, ...cardStyle, padding: "20px 18px 18px" }}>
									<div style={{
										width: 40, height: 40, borderRadius: 10,
										background: "#dbeafe",
										display: "flex", alignItems: "center", justifyContent: "center",
										marginBottom: 14,
									}}>
										{card.icon}
									</div>
									<Text style={{ fontSize: 16, color: "#6b7280", display: "block", marginBottom: 6, fontWeight: 500 }}>
										{card.label}
									</Text>
									<Title level={3} style={{ margin: 0, fontWeight: 700, fontSize: 22, color: "#111827" }}>
										{card.value}
									</Title>
								</div>
							))}
						</div>

						{/* Budget Comparison table */}
						<div style={{
							...cardStyle,
							overflow: "hidden",
							background: "#fff",
							borderRadius: 12,
						}}>
							{/* ── Card header ── */}
							<div style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "16px 20px 14px",
								borderBottom: "1px solid #f0f0f0",
							}}>
								<Text style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
									Budget Comparison
								</Text>

								<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
									{!isLocalCurrencyUSD && (
										<div style={{
											display: "flex",
											background: "#f3f4f6",
											borderRadius: 8,
											padding: 3,
											gap: 2,
										}}>
											{[
												{ label: currency, value: "local" },
												{ label: "USD", value: "usd" },
											].map(({ label, value }) => (
												<button
													key={value}
													onClick={() => setCurrencyView(value)}
													checked={true}
													onChange={true}
													style={{
														padding: "4px 14px",
														borderRadius: 6,
														border: "none",
														cursor: "pointer",
														fontSize: 13,
														fontWeight: 600,
														transition: "all 0.18s ease",
														background: currencyView === value ? "#374151" : "transparent",
														color: currencyView === value ? "#fff" : "#6b7280",
														boxShadow: currencyView === value ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
													}}
												>
													{label}
												</button>
											))}
										</div>
									)}
								</div>
							</div>

							{/* ── Table ── */}
							<Table
								dataSource={tableData}
								columns={columns}
								pagination={false}
								size="middle"
								style={{ borderRadius: 0 }}
							/>
						</div>
					</div>

					{/* RIGHT — Matter Details (unchanged) */}
					<div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
						<div style={{
							...cardStyle, padding: 24,
							height: "100%",
							maxHeight: "680px",
							overflowY: "auto",
						}}>
							<Title level={4} style={{ marginBottom: 18, marginTop: 0, fontWeight: 700, fontSize: 15 }}>
								Matter Details
							</Title>

							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
								<DetailField label="Client" value={clientName} />
								<DetailField label="Proposal ID" value={proposalId} />
								<DetailField label="Practice Area" value={practiceArea} />
								<DetailField label="Client reference number" value={clientReferenceNumber} />
								<DetailField label="Location" value={locationVal} />
								<div />
							</div>

							<Divider style={{ margin: "2px 0 16px", borderColor: "#e8e9ef" }} />

							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
								<DetailField label="Lead Attorney" value={leadAttorney} />
								<DetailField label="Referring Attorney" value={referringAttorney} />
								<DetailField label="Plan Start Date" value={planStartDate} />
								<DetailField label="Plan End Date" value={planEndDate} />
								<DetailField label="Legal Filing Deadline" value={legalFilingDeadline} />
								<div style={{ marginBottom: 16 }}>
									<Text style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 3 }}>
										Deadline met
									</Text>
									{deadlineMet === "true" ? <Text>Yes</Text> : <Text>No</Text>}
								</div>
							</div>

							<Divider style={{ margin: "2px 0 16px", borderColor: "#e8e9ef" }} />

							<Text style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>
								Objective
							</Text>
							<Text style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
								{objective}
							</Text>

							<Divider style={{ margin: "16px 0", borderColor: "#e8e9ef" }} />

							<Text style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>
								Responsible Personnel
							</Text>
							<div style={{ marginBottom: 16 }}>
								{(matterData?.responsiblePersonnel || [])?.length > 0 ? (
									matterData?.responsiblePersonnel.map((person, idx) => (
										<Tag
											key={idx}
											style={{
												background: "#d1fae5",
												border: "none",
												color: "#065f46",
												fontWeight: 500,
												marginBottom: 6,
											}}
										>
											{personLabel(person)}
										</Tag>
									))
								) : (
									<Text>—</Text>
								)}
							</div>

							<DetailField label="Billing Method" value={fmt(matterData?.billingMethod)} />

							{matterData?.billingMethod === "Hourly with Custom Rate" && (
								<div style={{ marginBottom: 16 }}>
									<Text style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 6 }}>
										Custom Rates per Employee
									</Text>
									{(matterData?.billingPreferenceEmployee || []).length > 0 ? (
										matterData.billingPreferenceEmployee.map((emp, idx) => (
											<div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
												<Text style={{ fontSize: 13 }}>{emp?.name || emp?.employeeName || "—"}</Text>
												<Text style={{ fontSize: 13, fontWeight: 600 }}>${emp?.rate ?? emp?.hourlyRate ?? "—"}/hr</Text>
											</div>
										))
									) : (
										<Text style={{ fontSize: 13 }}>—</Text>
									)}
								</div>
							)}

							{matterData?.billingMethod === "Flat Fee" && (
								<DetailField
									label="Flat Fee Amount"
									value={matterData?.billingFlatFee ? `$${Number(matterData.billingFlatFee).toLocaleString()}` : "—"}
								/>
							)}

							{matterData?.billingMethod === "Contingency Fee" && (
								<DetailField
									label="Contingency Fee Percentage"
									value={matterData?.billingContingencyFee ? `${matterData.billingContingencyFee}%` : "—"}
								/>
							)}

							{matterData?.billingMethod === "Hourly" && (() => {
								const bp = matterData?.billingPreference || {};
								const empRates = bp?.billingPreferenceEmployee || matterData?.billingPreferenceEmployee || [];
								if (empRates?.length > 0) {
									return (
										<div style={{ marginBottom: 16 }}>
											<Text style={{ fontSize: 12, color: "#9ca3af", display: "block", marginBottom: 8 }}>
												Custom Rate
											</Text>
											{empRates?.map((emp, idx) => {
												const userId = emp?.userId || emp?.userid || "";
												const found = userList?.find((u) => u.userid === userId || u?.personalemailid === userId);
												const fullName = found ? found?.fullname : userId || "—";
												return (
													<div key={idx} style={{ padding: "6px 0", borderBottom: idx < empRates?.length - 1 ? "1px solid #f3f4f6" : "none", width: "200px" }}>
														<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: idx < empRates?.length - 1 ? "1px solid #f3f4f6" : "none" }}>
															<Text style={{ fontSize: 13, color: "#374151" }}>{fullName}</Text>
															<Text style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
																${Number(emp.rate ?? emp.hourlyRate ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr
															</Text>
														</div>
													</div>
												);
											})}
										</div>
									);
								}
								return (
									<DetailField
										label="Hourly Rate"
										value={matterData?.billingHourlyRate ? `$${matterData.billingHourlyRate}/hr` : "—"}
									/>
								);
							})()}
						</div>
					</div>

				</div>
			</div>
		</>
	);
}